import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Icons } from '../lib/Icons';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { getStudentProgress } from '../lib/functions';
import SchoolPageHeader from '../components/school/SchoolPageHeader';
import { useSchoolCapabilities, SCHOOL_ACTIONS } from '../lib/auth/capabilities';
import { ALL_YEAR_GROUPS } from '../lib/curriculumAssessment';
import {
  PROGRESS_BAND_THRESHOLDS,
  countProgressBands,
  progressBandSummary,
} from '../lib/progressBands';

const ProgressDashboardPage = () => {
  const { students, loading: studentsLoading } = useStudents();
  const [loading, setLoading] = useState(true);
  const [yearGroupFilter, setYearGroupFilter] = useState('');
  const [atExpectedOnly, setAtExpectedOnly] = useState(false);
  const [studentsProgress, setStudentsProgress] = useState([]);
  const { can } = useSchoolCapabilities();

  useEffect(() => {
    if (!studentsLoading) {
      fetchStudentsProgress(students);
    }
  }, [studentsLoading, students]);

  const fetchStudentsProgress = async (studentsList) => {
    try {
      setLoading(true);
      
      // Get progress data for each student
      const studentsWithProgress = await Promise.all(
        studentsList.map(async (student) => {
          try {
            const result = await getStudentProgress({ studentId: student.id });
            return { ...student, progress: result.data };
          } catch (error) {
            console.error(`Error fetching progress for student ${student.id}:`, error);
            return { ...student, progress: null };
          }
        })
      );

      // Merge progress back into local derived list (not into hook)
      // We store it locally under the same state name for rendering table
      setStudentsProgress(studentsWithProgress);
    } catch (error) {
      console.error('Error fetching students progress:', error);
      toast.error('Failed to fetch progress data');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = useMemo(() => {
    let list = studentsProgress;
    if (yearGroupFilter) {
      list = list.filter((s) => s.yearGroup === yearGroupFilter);
    }
    if (atExpectedOnly) {
      list = list.filter((s) => s.progress?.curriculumProgress?.atExpectedOverall === true);
    }
    return list;
  }, [studentsProgress, yearGroupFilter, atExpectedOnly]);

  const overallStats = useMemo(() => {
    const studentsWithValidProgress = filteredStudents.filter((s) => s.progress);
    const totalProgress = studentsWithValidProgress.reduce(
      (sum, student) => sum + student.progress.overallProgress.percentageComplete,
      0,
    );
    const atExpectedCount = studentsWithValidProgress.filter(
      (s) => s.progress?.curriculumProgress?.atExpectedOverall === true,
    ).length;
    const withAttainment = studentsWithValidProgress.filter(
      (s) => s.progress?.curriculumProgress?.hasData,
    ).length;
    const bandCounts = countProgressBands(filteredStudents);
    const bands = progressBandSummary(bandCounts);

    return {
      totalStudents: filteredStudents.length,
      averageProgress:
        studentsWithValidProgress.length > 0
          ? Math.round(totalProgress / studentsWithValidProgress.length)
          : 0,
      bandCounts,
      bands,
      atExpectedPct:
        withAttainment > 0 ? Math.round((atExpectedCount / withAttainment) * 100) : 0,
      atExpectedCount,
      withAttainment,
    };
  }, [filteredStudents]);

  const getProgressColor = (percentage) => {
    if (percentage >= PROGRESS_BAND_THRESHOLDS.onTrack) return 'text-green-600';
    if (percentage >= PROGRESS_BAND_THRESHOLDS.goodProgress) return 'text-blue-600';
    if (percentage >= PROGRESS_BAND_THRESHOLDS.needsSupport) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getProgressBadge = (percentage) => {
    if (percentage >= PROGRESS_BAND_THRESHOLDS.onTrack) {
      return <Badge className="bg-green-100 text-green-800">On Track</Badge>;
    }
    if (percentage >= PROGRESS_BAND_THRESHOLDS.goodProgress) {
      return <Badge className="bg-blue-100 text-blue-800">Good Progress</Badge>;
    }
    if (percentage >= PROGRESS_BAND_THRESHOLDS.needsSupport) {
      return <Badge className="bg-yellow-100 text-yellow-800">Needs Support</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800">Requires Attention</Badge>;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        <SchoolPageHeader
          title="Progress Dashboard"
          description="Monitor subject-level attainment and intervention target progress across the SEND register."
          icon={Icons.ChartPie}
          breadcrumbs={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Progress' }]}
          actions={[
            can(SCHOOL_ACTIONS.VIEW_STUDENTS) ? { label: 'Student Directory', href: '/student-organization' } : null,
            can(SCHOOL_ACTIONS.REVIEW_APDR_QUEUE) ? { label: 'Open APDR in student profile', href: '/student-organization', variant: 'secondary' } : null,
          ].filter(Boolean)}
        />

        {/* Filters */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Year group</label>
              <select
                className="rounded-md border border-gray-300 px-3 py-2 text-sm"
                value={yearGroupFilter}
                onChange={(e) => setYearGroupFilter(e.target.value)}
              >
                <option value="">All year groups</option>
                {ALL_YEAR_GROUPS.map((yg) => (
                  <option key={yg} value={yg}>{yg}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
              <input
                type="checkbox"
                checked={atExpectedOnly}
                onChange={(e) => setAtExpectedOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              At expected only (all core subjects)
            </label>
            {yearGroupFilter && (
              <p className="text-sm text-indigo-800 bg-indigo-50 border border-indigo-200 rounded-md px-3 py-2">
                {overallStats.atExpectedCount} of {overallStats.withAttainment} pupils with attainment data
                are at expected in all core subjects ({overallStats.atExpectedPct}%)
              </p>
            )}
          </CardContent>
        </Card>

        {/* Overall Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600">
                  {overallStats.totalStudents}
                </div>
                <div className="text-sm text-gray-600">Total Students</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {overallStats.averageProgress}%
                </div>
                <div className="text-sm text-gray-600">Average Progress</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">
                  {overallStats.bands.onTrackOrGood}
                </div>
                <div className="text-sm text-gray-600">On track / good (≥60%)</div>
                {overallStats.bandCounts.goodProgress > 0 && (
                  <div className="text-xs text-gray-500 mt-1">
                    {overallStats.bandCounts.onTrack} on track (≥80%),{' '}
                    {overallStats.bandCounts.goodProgress} good (60–79%)
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600">
                  {overallStats.bands.needsSupport}
                </div>
                <div className="text-sm text-gray-600">Needs Support (40–59%)</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-red-600">
                  {overallStats.bands.requiresAttention}
                </div>
                <div className="text-sm text-gray-600">Requires Attention (&lt;40%)</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-gray-600">
                  {overallStats.bands.noData}
                </div>
                <div className="text-sm text-gray-600">No progress data</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <p className="text-xs text-gray-500 -mt-2">
          Progress bands: on track / good + needs support + requires attention + no data ={' '}
          {overallStats.bands.total} pupil{overallStats.bands.total === 1 ? '' : 's'} (filtered).
        </p>

        {/* Students Progress Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.Users className="w-5 h-5" /> Student Progress Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Student
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Year Group
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Overall Progress
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Curriculum
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      At expected
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Interventions
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                              <span className="text-sm font-medium text-blue-600">
                                {student.firstName?.charAt(0)?.toUpperCase() || '?'}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {student.firstName && student.lastName ? `${student.firstName} ${student.lastName}` : 'Unknown Student'}
                            </div>
                            <div className="text-sm text-gray-500">
                              {student.yearGroup || 'No Year Group'}
                            </div>
                          </div>
                        </div>
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {student.yearGroup || 'N/A'}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.progress ? (
                          <div className="text-sm">
                            <span className={`font-medium ${getProgressColor(student.progress.overallProgress.percentageComplete)}`}>
                              {student.progress.overallProgress.percentageComplete}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-500">No data</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.progress?.curriculumProgress?.hasData || student.progress?.curriculumProgress?.currentLevel ? (
                          <div className="text-sm">
                            <span className="text-gray-900">
                              {student.progress.curriculumProgress.frameworkLabel || 'Curriculum'}
                            </span>
                            <div className="text-xs text-gray-500">
                              {student.progress.curriculumProgress.percentageComplete}% subjects at expected
                            </div>
                          </div>
                        ) : student.progress ? (
                          <span className="text-sm text-gray-500">Awaiting teacher entry</span>
                        ) : (
                          <span className="text-sm text-gray-500">No data</span>
                        )}
                      </td>

                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.progress?.curriculumProgress?.hasData ? (
                          student.progress.curriculumProgress.atExpectedOverall ? (
                            <Badge className="bg-green-100 text-green-800">Yes</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-800">Not yet</Badge>
                          )
                        ) : (
                          <span className="text-sm text-gray-500">Not recorded</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.progress?.interventionProgress?.hasData ? (
                          <div className="text-sm">
                            <span className="text-gray-900">
                              {student.progress.interventionProgress.achievedCount}/{student.progress.interventionProgress.totalTargetScores} targets met
                            </span>
                            <div className="text-xs text-gray-500">
                              {student.progress.interventionProgress.percentageComplete}% attainment
                            </div>
                          </div>
                        ) : student.progress?.interventionProgress?.hasSessionLogs ? (
                          <div className="text-sm text-gray-500">
                            <span>{student.progress.interventionProgress.attendedSessions ?? 0} session(s) logged</span>
                            <div className="text-xs">No target scores recorded yet</div>
                          </div>
                        ) : student.progress ? (
                          <span className="text-sm text-gray-500">No intervention sessions logged</span>
                        ) : (
                          <span className="text-sm text-gray-500">No data</span>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap">
                        {student.progress ? (
                          getProgressBadge(student.progress.overallProgress.percentageComplete)
                        ) : (
                          <Badge className="bg-gray-100 text-gray-800">No Data</Badge>
                        )}
                      </td>
                      
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/student/${student.id}`}
                          className="text-blue-600 hover:text-blue-900 mr-4"
                        >
                          View Profile
                        </Link>
                        <Link
                          to={`/student/${student.id}#progress`}
                          className="text-green-600 hover:text-green-900"
                        >
                          Progress Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.Bolt className="w-5 h-5" /> Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Link
                to="/student-organization"
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="mb-2 flex justify-center"><Icons.Users className="w-8 h-8 text-gray-600" /></div>
                <div className="font-medium text-gray-900">Manage Students</div>
                <div className="text-sm text-gray-500">Add, edit, or remove students</div>
              </Link>
              
              <Link
                to="/interventions"
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="mb-2 flex justify-center"><Icons.Target className="w-8 h-8 text-gray-600" /></div>
                <div className="font-medium text-gray-900">Intervention Management</div>
                <div className="text-sm text-gray-500">Create and monitor interventions</div>
              </Link>
              
              <Link
                to="/isps"
                className="p-4 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-blue-400 hover:bg-blue-50 transition-colors"
              >
                <div className="mb-2 flex justify-center"><Icons.BookOpen className="w-8 h-8 text-gray-600" /></div>
                <div className="font-medium text-gray-900">Open Individual Support Plans</div>
                <div className="text-sm text-gray-500">Review and update pupil support plans</div>
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* Related Sections */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.Link className="w-5 h-5" /> Related Sections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Icons.ChartBar className="w-5 h-5" /> Individual Student Progress
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  View detailed progress for individual students including curriculum targets and intervention attendance.
                </p>
                <Link
                  to="/student-organization"
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  View Students →
                </Link>
              </div>
              
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Icons.ArrowTrendingUp className="w-5 h-5" /> Progress Reports
                </h4>
                <p className="text-sm text-gray-600 mb-3">
                  Generate comprehensive progress reports for individual students or the entire cohort.
                </p>
                <span className="text-gray-500 text-sm">
                  Available in student profiles
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icons.ClipboardDocumentCheck className="w-5 h-5" /> APDR and Review Pack Workflow
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Step 1</p>
                <h4 className="mt-1 font-semibold text-slate-900">Create evidence snapshot</h4>
                <p className="mt-1 text-sm text-slate-600">From each student profile, generate APDR evidence to capture the latest cycle.</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Step 2</p>
                <h4 className="mt-1 font-semibold text-slate-900">Review SENCO queue</h4>
                <p className="mt-1 text-sm text-slate-600">SENCO approves target-review queue items before they become termly targets.</p>
              </div>
              <div className="rounded-lg border border-slate-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-indigo-600">Step 3</p>
                <h4 className="mt-1 font-semibold text-slate-900">Generate review pack</h4>
                <p className="mt-1 text-sm text-slate-600">Produce parent-friendly or statutory packs using validated progress data.</p>
              </div>
            </div>
            <div className="mt-4">
              <Link
                to="/student-organization"
                className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
              >
                Open student profiles to run APDR workflow
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
};

export default ProgressDashboardPage; 
